'use client';

import * as React from 'react';
import { XIcon } from 'lucide-react';

import { cn, useIsMobile } from '@/src/shared/lib/shadcn';
import { BottomSheet } from './bottom-sheet';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface AdaptivePopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The control that opens the panel; it keeps aria-expanded in both forms. */
  trigger: React.ReactElement;
  title: string;
  closeLabel: string;
  align?: 'start' | 'center' | 'end';
  /** Width class of the desktop popover, 380px by default. */
  widthClassName?: string;
  /** Opens with focus on the title instead of the first control, for a panel that is only read. */
  focusTitle?: boolean;
  /** Ends the phone sheet with a full-width close button, for a panel with no action of its own. */
  sheetClose?: boolean;
  children: React.ReactNode;
}

/**
 * A labelled panel anchored to its trigger on desktop and a bottom sheet on
 * phones. Both roots stay mounted so the viewport flag settling after mount
 * does not remount the trigger.
 */
function AdaptivePopover({
  open,
  onOpenChange,
  trigger,
  title,
  closeLabel,
  align = 'start',
  widthClassName = 'w-[380px]',
  focusTitle = false,
  sheetClose = false,
  children,
}: AdaptivePopoverProps) {
  const isMobile = useIsMobile();
  const titleId = React.useId();
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  return (
    <Popover open={open && !isMobile} onOpenChange={onOpenChange}>
      <PopoverTrigger ref={triggerRef} asChild aria-expanded={open}>
        {trigger}
      </PopoverTrigger>
      {isMobile ? (
        <BottomSheet
          open={open}
          onOpenChange={onOpenChange}
          title={title}
          focusTitle={focusTitle}
          // The sheet is a dialog with no trigger of its own, so Radix would hand focus to the body.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            triggerRef.current?.focus();
          }}
        >
          {children}
          {sheetClose && (
            <Button
              variant="subtle"
              size="action"
              onClick={() => onOpenChange(false)}
              className="w-full"
            >
              {closeLabel}
            </Button>
          )}
        </BottomSheet>
      ) : (
        <PopoverContent
          align={align}
          sideOffset={10}
          role="dialog"
          aria-labelledby={titleId}
          onOpenAutoFocus={
            focusTitle
              ? (event) => {
                  event.preventDefault();
                  titleRef.current?.focus();
                }
              : undefined
          }
          className={cn(
            'flex max-w-[calc(100vw-2rem)] flex-col gap-5 rounded-[18px] border-0 bg-(--sc-pop) p-6',
            'shadow-(--sc-shadow-pop) [corner-shape:squircle] motion-reduce:animate-none',
            widthClassName,
          )}
        >
          <div className="flex items-center justify-between">
            <h2
              id={titleId}
              ref={titleRef}
              tabIndex={focusTitle ? -1 : undefined}
              className="text-title outline-none"
            >
              {title}
            </h2>
            <button
              type="button"
              aria-label={closeLabel}
              onClick={() => onOpenChange(false)}
              className="-my-3 -mr-3 grid size-11 cursor-pointer place-items-center rounded-full text-muted-foreground outline-none hover:bg-(--sc-subtle) focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-(--sc-ring)"
            >
              <XIcon className="size-4" aria-hidden />
            </button>
          </div>
          {children}
        </PopoverContent>
      )}
    </Popover>
  );
}

export { AdaptivePopover };
